import { useAuthStore, type RefreshResult } from '../store/auth.store';
import { useAceitePendenteStore } from '../store/aceite-pendente.store';
import { useAcessoStore, temAtribuicaoRbac } from '../store/acesso.store';
import { useEstabelecimentoAtivoStore } from '../store/estabelecimento-ativo.store';
import { podeComAcesso } from '../hooks/use-permissao';
import { leituraDaTela } from './telas-acesso';

/**
 * Wrapper de fetch para requisições autenticadas (REQ-003 / AB#23).
 *
 * - Anexa o access_token do store no header Authorization.
 * - Em 401, tenta renovar a sessão (POST /auth/refresh) uma única vez e repete
 *   a requisição original com o novo token.
 * - **Deduplicação de refresh concorrente**: se múltiplas requisições estouram
 *   401 em paralelo (cenário comum em telas que disparam vários fetches no
 *   mount, como /audit-log), todas aguardam a MESMA Promise de refresh. Sem
 *   isso, o segundo+ refresh chega ao backend depois da rotação do refresh
 *   token e falha, derrubando a sessão inteira por engano.
 * - Se a renovação falhar por inatividade (AB#24), redireciona para /login com
 *   ?reason=inactivity (banner na tela). Outras falhas → /login sem motivo.
 *
 * [#916 / ADR-069 §13.3 / D10 #915] `apiFetch` envia `X-Estabelecimento-Id` em
 * toda requisição a partir do estado GLOBAL (`useEstabelecimentoAtivoStore`,
 * escrito pelo único `<SeletorEstabelecimento>` do `AppShell`) — `null` ("Todas")
 * omite o header, que o backend já trata como "todos os permitidos"
 * (`ContextoAcessoInterceptor`, `contexto-acesso.service.ts:270-301`). Só manda o
 * header quando o usuário tem pelo menos uma `UsuarioAcesso`
 * (`temAtribuicaoRbac(acesso)` de `store/acesso.store.ts`) — sem atribuição, o
 * interceptor nega qualquer unidade com 403 (ainda não passou pelo backfill),
 * então mandar o header pra ele seria regressão, não melhoria.
 */

// Backend rotas Nest ficam todas sob `/api/*` (setGlobalPrefix em main.ts).
// `VITE_API_URL` aponta para o host (sem `/api`) — o sufixo é fixo do
// backend. Static files (`/uploads/chamados/*`) usam `VITE_API_URL` direto,
// sem `/api` — ver `components/anexos/AnexosLista.tsx`.
export const API_URL = `${import.meta.env.VITE_API_URL ?? 'http://localhost:8000'}/api`;

/**
 * [Bug — link do PDF da nota não abria] URLs de arquivo estático em DEV vêm
 * relativas (`/uploads/...`) — o navegador resolve `href`/`src` relativo
 * contra a origem da PÁGINA (o Vite, :5173), não contra o backend que
 * serve o arquivo de verdade (:8000). Um `<a href="/uploads/...">` cru
 * navega pro Vite, que devolve o fallback de SPA — não o arquivo, sem erro
 * visível ("clicar não abre nada"). Em QA/PROD a URL já vem absoluta (S3,
 * `FileStorageService.urlAcesso`) e passa direto.
 *
 * Extraído de `AnexosLista.tsx` (mesma armadilha, resolvida lá primeiro) —
 * reusar aqui em vez de duplicar o helper pela segunda vez.
 *
 * [Bug — modal de criação de nota] `FormularioNotaFiscal.pdfExistente.url`
 * também recebe URL `blob:` local (PDF escolhido mas ainda não enviado ao
 * servidor — `TitulosPage.tsx`, nota nova) além da URL real do backend
 * (nota já salva). `startsWith('http')` sozinho NÃO cobre `blob:...` —
 * prefixaria e quebraria o preview local. `data:` incluído pela mesma
 * cautela (nunca apareceu aqui ainda, mas é outra URL "já completa").
 */
export function resolverUrlEstatica(url: string): string {
  if (url.startsWith('http') || url.startsWith('blob:') || url.startsWith('data:')) return url;
  const base = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:8000';
  return `${base}${url}`;
}

/**
 * [Bug#756 — fix estrutural] `body` string sem `Content-Type` explícito faz o
 * browser mandar `text/plain;charset=UTF-8` por padrão — o parser de JSON do
 * Fastify só roda quando o Content-Type declara isso. Sem ele, o body chega
 * vazio no DTO: campos opcionais passam a validação, o handler devolve 200,
 * e nada é gravado. Sintoma: "salvei e nada mudou" sem erro nenhum na tela.
 *
 * Corrigido aqui, uma vez, em vez de em cada chamador — nenhum chamador
 * LEGÍTIMO manda `body` string sem querer dizer JSON (o único caso real de
 * `body` não-string no projeto é upload, `FormData`, que precisa do
 * Content-Type AUSENTE — o browser injeta `multipart/form-data;
 * boundary=...` sozinho, e setar à mão quebraria o parser). Por isso o
 * default só entra quando `body` é `string` E o chamador não trouxe seu
 * próprio Content-Type (built-in ou explícito) — nunca sobrescreve.
 */
function temContentType(headers: HeadersInit | undefined): boolean {
  if (!headers) return false;
  if (headers instanceof Headers) return headers.has('Content-Type');
  if (Array.isArray(headers)) return headers.some(([k]) => k.toLowerCase() === 'content-type');
  return Object.keys(headers).some((k) => k.toLowerCase() === 'content-type');
}

function comAuth(init: RequestInit, token: string | null): RequestInit {
  const comTipo: RequestInit =
    typeof init.body === 'string' && !temContentType(init.headers)
      ? { ...init, headers: { ...init.headers, 'Content-Type': 'application/json' } }
      : init;
  const comToken = token
    ? { ...comTipo, headers: { ...comTipo.headers, Authorization: `Bearer ${token}` } }
    : comTipo;
  return comEstabelecimentoAtivo(comToken);
}

/** [#916] Ver docstring de `apiFetch` — só manda o header com RBAC (D10 #915). */
function comEstabelecimentoAtivo(init: RequestInit): RequestInit {
  if (!temAtribuicaoRbac(useAcessoStore.getState().acesso)) return init;
  const id = useEstabelecimentoAtivoStore.getState().estabelecimentoAtivoId;
  if (id === null) return init;
  return { ...init, headers: { ...init.headers, 'X-Estabelecimento-Id': String(id) } };
}

/** Refresh atualmente em vôo. Compartilhado entre todas as chamadas concorrentes. */
let inflightRefresh: Promise<RefreshResult> | null = null;

function refreshDedup(): Promise<RefreshResult> {
  if (inflightRefresh) return inflightRefresh;
  inflightRefresh = useAuthStore
    .getState()
    .refreshSession()
    .finally(() => {
      inflightRefresh = null;
    });
  return inflightRefresh;
}

export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const store = useAuthStore.getState();
  const url = path.startsWith('http') ? path : `${API_URL}${path}`;

  let res = await fetch(url, comAuth(init, store.access_token));
  if (res.status === 403) {
    // [AB#1282] Senha provisória: nada funciona até trocar — leva à tela de troca obrigatória
    // (exceto se já estiver nela, pra não recarregar em loop).
    if (await isTrocaSenhaObrigatoriaResponse(res)) {
      useAuthStore.getState().setSenhaProvisoria(true);
      if (window.location.pathname !== '/trocar-senha') {
        window.location.href = '/trocar-senha';
      }
      return res;
    }
    // Trial expirado (REQ-008 Fluxo 4 / AB#47): backend devolve
    // { code: 'TRIAL_EXPIRADO', ... }.
    //
    // [Bug#357] Destino é a ASSINATURA, não `/trial-expirado`. Esta última é a
    // tela de ATERRISSAGEM DO LOGIN (o Login navega pra lá quando o corpo traz
    // `trial_expirado`) — serve pra dar a notícia e a data de exclusão dos dados.
    // Quem JÁ está dentro e tenta abrir um módulo não precisa da notícia de novo:
    // precisa é do lugar onde resolve. Mandar pra tela informativa era um beco —
    // ele lia o aviso e tinha que clicar de novo pra chegar onde importa.
    //
    // Não redireciona quando ele já está numa rota de resolução (ROTAS_DE_RESOLUCAO):
    // os componentes globais do shell disparam 403 em paralelo e derrubariam a
    // própria tela de assinatura.
    //
    // [AB#907] Mandar pra assinatura só ajuda quem tem `config.assinatura:ler` (hoje,
    // de fábrica, só Administrador) — pra quem não tem, a tela de assinatura é só um
    // 403 (`AcessoNegado`) na frente, outro beco. Sem a permissão: não navega, só
    // atualiza a flag do store — o `TenantLayout` (fonte única deste gate, mesmo
    // critério) re-renderiza sozinho com a tela de aviso, sem reload, sem loop.
    if (await isTrialExpiradoResponse(res)) {
      if (!ehRotaDeResolucao(window.location.pathname)) {
        if (podeComAcesso(useAcessoStore.getState().acesso, leituraDaTela('mc-ass'))) {
          window.location.href = '/minha-conta/assinatura#modulos';
        } else {
          useAuthStore.getState().setTrialExpirado(true);
        }
      }
      return res;
    }
    // Conta suspensa por inadimplência (REQ-010 / AB#77): backend devolve
    // { code: 'CONTA_SUSPENSA', ... }. Redireciona para a tela informativa,
    // exceto se o usuário JÁ está em uma "ilha" permitida — `/conta-suspensa`
    // ou `/minha-conta/chamados`. Sem essa allowlist, componentes globais
    // (SinoNotificacoes, ModulosBoot, PropostaSegmentoBanner, ReleaseNotesModal)
    // que disparam requests em paralelo ao montar `/minha-conta/chamados`
    // batem 403 nessas rotas não-marcadas e empurram o usuário de volta
    // para `/conta-suspensa` — bloqueando o único canal de suporte.
    if (await isContaSuspensaResponse(res)) {
      if (!ehIlhaPermitidaSuspenso(window.location.pathname)) {
        window.location.href = '/conta-suspensa';
      }
      return res;
    }
    // Aceite pendente de nova versão MAJOR (REQ-012 Adendo / AB#63):
    // popula store para abrir o modal global; o caller recebe 403 e
    // pode ignorar (a UI fica bloqueada pelo modal).
    const aceite = await getAceitePendenteBody(res);
    if (aceite) {
      useAceitePendenteStore.getState().setPendente(aceite);
      return res;
    }
    // Módulo não contratado (REQ-009 Adendo / AB#67): redireciona para a
    // tela de upgrade com o código do módulo bloqueado no querystring.
    //
    // [Bug#357] NÃO redireciona quando o usuário já está numa rota de resolução
    // (assinatura / chamados / trial-expirado). Mesma razão do ramo logo abaixo
    // (MODULO_TRIAL_EXPIRADO, que já se protegia disso): os componentes globais
    // do shell batem em rotas com @Modulo() ao montar, e o tenant vencido não
    // tem módulo nenhum ativo — o 403 arrastava a tela de assinatura pro
    // /upgrade. Quem está na página onde contrata não pode ser expulso dela.
    const modulo = await getModuloNaoContratadoBody(res);
    if (
      modulo &&
      !window.location.pathname.startsWith('/upgrade') &&
      !ehRotaDeResolucao(window.location.pathname)
    ) {
      window.location.href = `/upgrade?modulo=${encodeURIComponent(modulo)}`;
      return res;
    }
    // Trial individual do módulo expirou ([UI-ASSINATURA]): manda pra
    // /minha-conta/assinatura com o código no querystring — a tela única
    // mostra Callout danger no topo e dá scroll na seção Módulos. Não
    // redireciona se o user já está dentro de /minha-conta/assinatura
    // (evita loop quando as próprias telas batem em rota com @Modulo()).
    //
    // [AB#907] Mesma regra do TRIAL_EXPIRADO acima: só navega pra assinatura se o
    // usuário tiver `config.assinatura:ler`. Sem ela, não é um bloqueio de tenant
    // inteiro (só este módulo) — não navegar basta, a página/banner que disparou a
    // chamada já degrada sozinha (ver `ModuloTrialBanner`).
    const moduloTrialExp = await getModuloTrialExpiradoBody(res);
    if (
      moduloTrialExp &&
      !window.location.pathname.startsWith('/minha-conta/assinatura') &&
      podeComAcesso(useAcessoStore.getState().acesso, leituraDaTela('mc-ass'))
    ) {
      window.location.href = `/minha-conta/assinatura?modulo_trial_expirado=${encodeURIComponent(moduloTrialExp)}#modulos`;
      return res;
    }
  }
  if (res.status !== 401) return res;

  // Token expirado: tenta renovar UMA vez. Concorrentes aguardam a mesma Promise.
  const result = await refreshDedup();
  if (!result.ok) {
    useAuthStore.getState().clear();
    // Inatividade vai com motivo no querystring p/ banner na tela (AB#24).
    window.location.href =
      result.reason === 'inactivity' ? '/login?reason=inactivity' : '/login';
    return res;
  }

  res = await fetch(url, comAuth(init, useAuthStore.getState().access_token));
  return res;
}

/**
 * Lê e clona o body 403 para detectar `code === 'TRIAL_EXPIRADO'` sem consumir
 * a resposta original (o chamador ainda pode chamar .json() depois).
 */
/** [AB#1282] 403 `TROCA_SENHA_OBRIGATORIA`: conta ainda com a senha provisória. */
async function isTrocaSenhaObrigatoriaResponse(res: Response): Promise<boolean> {
  try {
    const body = (await res.clone().json()) as { code?: string } | null;
    return body?.code === 'TROCA_SENHA_OBRIGATORIA';
  } catch {
    return false;
  }
}

async function isTrialExpiradoResponse(res: Response): Promise<boolean> {
  try {
    const body = (await res.clone().json()) as { code?: string } | null;
    return body?.code === 'TRIAL_EXPIRADO';
  } catch {
    return false;
  }
}

/** Detecta 403 com code='CONTA_SUSPENSA' (REQ-010 / AB#77). */
async function isContaSuspensaResponse(res: Response): Promise<boolean> {
  try {
    const body = (await res.clone().json()) as { code?: string } | null;
    return body?.code === 'CONTA_SUSPENSA';
  } catch {
    return false;
  }
}

/**
 * Rotas onde o tenant SUSPENSO pode ficar sem ser empurrado de volta para
 * `/conta-suspensa`. O conjunto precisa cobrir o canal de suporte para que
 * o tenant ainda consiga falar com a equipe (AB#77).
 *
 * Quando o user está numa dessas rotas e o interceptor vê um 403
 * `CONTA_SUSPENSA` de uma request paralela (sino, módulos, banners...),
 * silenciamos sem redirecionar — os componentes globais já degradam
 * para estado vazio quando o backend recusa.
 */
const ROTAS_ILHA_SUSPENSO: readonly string[] = [
  '/conta-suspensa',
  '/minha-conta/chamados',
];

function ehIlhaPermitidaSuspenso(pathname: string): boolean {
  return ROTAS_ILHA_SUSPENSO.some(
    (rota) => pathname === rota || pathname.startsWith(`${rota}/`),
  );
}

/**
 * [Bug#357] Rotas de RESOLUÇÃO — onde o usuário está justamente consertando a
 * pendência que o bloqueia. **Nunca redirecionar para fora delas.**
 *
 * Mesmo conceito das ilhas de conta suspensa (ROTAS_ILHA_SUSPENSO, AB#77), que
 * nasceu do mesmo bug. O tenant com trial vencido ficara sem esse tratamento —
 * porque, até este fix, ele nem chegava a ter sessão.
 *
 * Sintoma: ele clicava em "Ver planos", a tela de assinatura carregava e num
 * piscar era arrastada embora. O GET da assinatura passa (tem
 * `@AllowTrialExpired`), mas os componentes GLOBAIS do shell (sino, ModulosBoot,
 * banners, release notes) disparam requests em paralelo ao montar, e batem em
 * rotas que o tenant vencido não pode acessar — de duas formas:
 *   1. rotas sem `@AllowTrialExpired` → 403 TRIAL_EXPIRADO      → ia pra /trial-expirado
 *   2. rotas com `@Modulo()` (ele não tem módulo ativo) → 403 MODULO_NAO_CONTRATADO
 *      → ia pra /upgrade
 * Nos dois casos, a tela onde ele contrataria era destruída.
 *
 * Aqui silenciamos o redirect: os componentes globais já degradam pra estado
 * vazio quando o backend recusa. **A proteção real continua no backend** — esta
 * lista só impede o front de se auto-sabotar.
 */
const ROTAS_DE_RESOLUCAO: readonly string[] = [
  '/trial-expirado',
  '/minha-conta/assinatura',
  '/minha-conta/chamados',
];

export function ehRotaDeResolucao(pathname: string): boolean {
  return ROTAS_DE_RESOLUCAO.some((rota) => pathname === rota || pathname.startsWith(`${rota}/`));
}

/** Detecta 403 com code='MODULO_NAO_CONTRATADO' e devolve o código do módulo (AB#67). */
async function getModuloNaoContratadoBody(res: Response): Promise<string | null> {
  try {
    const body = (await res.clone().json()) as { code?: string; modulo?: string } | null;
    if (body?.code === 'MODULO_NAO_CONTRATADO' && typeof body.modulo === 'string') {
      return body.modulo;
    }
    return null;
  } catch {
    return null;
  }
}

/** Detecta 403 com code='MODULO_TRIAL_EXPIRADO' e devolve o código ([UI-ASSINATURA]). */
async function getModuloTrialExpiradoBody(res: Response): Promise<string | null> {
  try {
    const body = (await res.clone().json()) as { code?: string; modulo?: string } | null;
    if (body?.code === 'MODULO_TRIAL_EXPIRADO' && typeof body.modulo === 'string') {
      return body.modulo;
    }
    return null;
  } catch {
    return null;
  }
}

/** Detecta 403 com code='ACEITE_PENDENTE' e devolve o payload (AB#63). */
async function getAceitePendenteBody(res: Response): Promise<{
  documento_id: number;
  tipo: string;
  versao: string;
} | null> {
  try {
    const body = (await res.clone().json()) as {
      code?: string;
      documento_id?: number;
      tipo?: string;
      versao?: string;
    } | null;
    if (
      body?.code === 'ACEITE_PENDENTE' &&
      typeof body.documento_id === 'number' &&
      typeof body.tipo === 'string' &&
      typeof body.versao === 'string'
    ) {
      return { documento_id: body.documento_id, tipo: body.tipo, versao: body.versao };
    }
    return null;
  } catch {
    return null;
  }
}
